import {
  createAppDirectAuth,
  AuthError,
  type AppDirectAuthConfig,
  type CookieMutation,
} from '../../index.js';
import { applyCookieMutations, clearCookieMutations, readAuthCookies } from '../shared.js';
import { appHomeUrl, errorRedirectUrl } from '../redirects.js';
import { assertSameSiteFetchRequest, readBodySessionTokens } from '../same-site.js';

type NextResponseType = {
  redirect(url: string | URL, status?: number): NextResponseType;
  json(body: unknown, init?: { status?: number }): NextResponseType;
  headers: {
    set(name: string, value: string): void;
    append(name: string, value: string): void;
  };
  cookies: {
    set(
      name: string,
      value: string,
      options?: {
        httpOnly?: boolean;
        sameSite?: 'lax' | 'strict' | 'none';
        secure?: boolean;
        path?: string;
        maxAge?: number;
      },
    ): void;
  };
};

type NextResponseConstructor = {
  redirect(url: string | URL, status?: number): NextResponseType;
  json(body: unknown, init?: { status?: number }): NextResponseType;
};

function getNextResponse(): NextResponseConstructor {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const nextServer = require('next/server') as { NextResponse: NextResponseConstructor };
    return nextServer.NextResponse;
  } catch {
    throw new Error(
      'Next.js is required for @appdirect/auth-bff/next. Install next as a peer dependency.',
    );
  }
}

function applyCookiesToNextResponse(
  response: NextResponseType,
  mutations: CookieMutation[],
): void {
  applyCookieMutations(
    {
      setHeader: (name, value) =>
        response.headers.set(name, Array.isArray(value) ? value.join(', ') : value),
      appendHeader: (name, value) => response.headers.append(name, value),
    },
    mutations,
  );
}

export function createNextAuthHandlers(config: AppDirectAuthConfig) {
  const auth = createAppDirectAuth(config);
  const NextResponse = getNextResponse();

  return {
    login(req: Request): NextResponseType {
      if (req.method !== 'GET') {
        return NextResponse.json({ error: 'Method not allowed' }, { status: 405 });
      }

      try {
        const result = auth.buildLoginRedirect();
        const response = NextResponse.redirect(result.authorizeUrl, 302);
        applyCookiesToNextResponse(response, result.cookiesToSet);
        return response;
      } catch (err: unknown) {
        const code = err instanceof AuthError ? err.code : 'server_config';
        return NextResponse.json({ error: code }, { status: 500 });
      }
    },

    async callback(req: Request): Promise<NextResponseType> {
      if (req.method !== 'GET') {
        return NextResponse.json({ error: 'Method not allowed' }, { status: 405 });
      }

      const url = new URL(req.url);
      const code = url.searchParams.get('code') ?? undefined;
      const error = url.searchParams.get('error') ?? undefined;
      const state = url.searchParams.get('state') ?? undefined;
      const { store } = readAuthCookies(req.headers.get('cookie'));

      if (error || !code) {
        return NextResponse.redirect(errorRedirectUrl(auth.config, error ?? 'no_code'), 302);
      }

      try {
        const result = await auth.handleCallback({ code, state, cookies: store });
        const response = NextResponse.redirect(appHomeUrl(auth.config), 302);
        applyCookiesToNextResponse(response, result.cookiesToSet);
        applyCookiesToNextResponse(
          response,
          clearCookieMutations(auth.config, result.cookiesToClear),
        );
        return response;
      } catch (err: unknown) {
        const code = err instanceof AuthError ? err.code : 'server_config';
        return NextResponse.redirect(errorRedirectUrl(auth.config, code), 302);
      }
    },

    async me(req: Request): Promise<NextResponseType> {
      if (req.method !== 'GET') {
        return NextResponse.json({ error: 'Method not allowed' }, { status: 405 });
      }

      const { session, refreshToken } = readAuthCookies(req.headers.get('cookie'));
      const result = await auth.getMe({ sessionToken: session, refreshToken });
      const response = NextResponse.json({
        user: result.user,
        hasSession: result.hasSession,
      });
      if (result.cookiesToSet?.length) {
        applyCookiesToNextResponse(response, result.cookiesToSet);
      }
      return response;
    },

    async jwt(req: Request): Promise<NextResponseType> {
      if (req.method !== 'GET') {
        return NextResponse.json({ error: 'Method not allowed' }, { status: 405 });
      }

      const { session, refreshToken } = readAuthCookies(req.headers.get('cookie'));

      try {
        const result = await auth.getFeJwt({ sessionToken: session, refreshToken });
        const response = NextResponse.json({ token: result.token });
        if (result.cookiesToSet?.length) {
          applyCookiesToNextResponse(response, result.cookiesToSet);
        }
        return response;
      } catch (err: unknown) {
        if (err instanceof AuthError) {
          return NextResponse.json(
            {
              error: err.code,
              token: null,
              refresh_url: auth.config.routes.refresh,
            },
            { status: err.statusCode },
          );
        }
        return NextResponse.json({ error: 'server_config', token: null }, { status: 500 });
      }
    },

    async refresh(req: Request): Promise<NextResponseType> {
      if (req.method !== 'GET' && req.method !== 'POST') {
        return NextResponse.json({ error: 'Method not allowed' }, { status: 405 });
      }

      const { refreshToken } = readAuthCookies(req.headers.get('cookie'));
      if (!refreshToken) {
        return NextResponse.json({ error: 'not_authenticated', ok: false }, { status: 401 });
      }

      try {
        const result = await auth.refreshSession({ refreshToken });
        const response = NextResponse.json({ ok: true, token: result.fejwt });
        applyCookiesToNextResponse(response, result.cookiesToSet);
        return response;
      } catch {
        return NextResponse.json({ error: 'refresh_failed', ok: false }, { status: 502 });
      }
    },

    logout(req: Request): NextResponseType {
      if (req.method !== 'GET' && req.method !== 'POST') {
        return NextResponse.json({ error: 'Method not allowed' }, { status: 405 });
      }

      const response = NextResponse.redirect(appHomeUrl(auth.config), 302);
      applyCookiesToNextResponse(
        response,
        clearCookieMutations(auth.config, auth.buildLogout().cookiesToClear),
      );
      return response;
    },

    async importSession(req: Request): Promise<NextResponseType> {
      if (req.method !== 'POST') {
        return NextResponse.json({ error: 'Method not allowed' }, { status: 405 });
      }
      if (!assertSameSiteFetchRequest(req, auth.config.appBaseUrl)) {
        return NextResponse.json({ error: 'Cross-site request blocked' }, { status: 403 });
      }

      let body: unknown;
      try {
        body = await req.json();
      } catch {
        return NextResponse.json({ error: 'invalid_body' }, { status: 400 });
      }

      const tokens = readBodySessionTokens(body);
      if (!tokens) {
        return NextResponse.json({ error: 'invalid_body' }, { status: 400 });
      }

      try {
        const result = await auth.importSession(tokens);
        const response = NextResponse.json({ ok: true });
        applyCookiesToNextResponse(response, result.cookiesToSet);
        return response;
      } catch (err: unknown) {
        if (err instanceof AuthError) {
          return NextResponse.json({ error: err.code }, { status: err.statusCode });
        }
        return NextResponse.json({ error: 'server_config' }, { status: 500 });
      }
    },

    auth,
  };
}

export type { AppDirectAuthConfig };
